/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/camelcase */
import { Injectable, ErrorHandler } from "@angular/core";
import { BehaviorSubject, Observable, interval } from "rxjs";
import { ErrorResponse } from "./api.service";
import { UserService } from "./user.service";
import { KeycloakAuthService } from "./keycloak-auth.service";

import { User } from "./../models/User.model";

import storageX from "../storageCore/storageX";
import { handleUnauthorizedRedirect } from "../utils/unauthorized-handler";
import {
  STORAGE_KEY_CD_USUARIO,
  STORAGE_KEY_COMANDOS_EFETIVOS,
  STORAGE_KEY_CURRENT_PERFIL,
  STORAGE_KEY_CURRENT_USER,
  STORAGE_KEY_ID_MANAGER,
  STORAGE_KEY_ID_PERFIL,
  STORAGE_KEY_ID_SUPERVISOR,
  STORAGE_KEY_UUID_MANAGER,
  STORAGE_KEY_UUID_SUPERVISOR,
} from "../storageCore/constStorageKeys";

@Injectable({
  providedIn: "root",
})
export class AuthenticationService {
  public intervalTimeInMilliseconds = 10 * 60 * 1000; // 10 minutes in milliseconds

  private currentUserSubject: BehaviorSubject<User>;
  public currentUser: Observable<User>;
  private errorHandler: ErrorHandler;

  constructor(
    errorHandler: ErrorHandler,
    private userService: UserService,
    private keycloakAuthService: KeycloakAuthService
  ) {
    this.errorHandler = errorHandler;
    this.currentUserSubject = new BehaviorSubject<User>(
      storageX.whereKeyIs(STORAGE_KEY_CURRENT_USER).get() //JSON.parse(localStorage.getItem('currentUser'))
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  public async login(username: string, password: string): Promise<User> {
    try {
      const formData = new FormData();
      formData.set("username", username);
      formData.set("password", password);
      formData.set("grant_type", "password");
      storageX.whereKeyIs(STORAGE_KEY_CD_USUARIO).set(username);
      const user = await this.userService.login(username, password);
      if (!user) {
        return Promise.reject(
          this.managerError("Nao foi possivel obter o usuario")
        );
      }
      console.log(user);
      this.persistUserMetadata(user);
      const userParse = storageX.whereKeyIs(STORAGE_KEY_CURRENT_USER).set(user); //JSON.stringify(user);
      const hasUser = storageX.whereKeyIs(STORAGE_KEY_CURRENT_USER).get(); //JSON.parse(localStorage.getItem('currentUser'));
      if (!hasUser) {
        return Promise.reject(
          this.managerError("Usuario nao foi salvo corretamente")
        );
      }
      this.currentUserSubject.next(user);
      return user;
    } catch (error) {
      return Promise.reject(this.managerError(error));
    }
  }

  public async syncUserFromKeycloak(): Promise<User> {
    try {
      await this.keycloakAuthService.updateToken();
      const username =
        this.keycloakAuthService.getUsername() ||
        this.keycloakAuthService.getLegacyUserId();

      if (!username) {
        return Promise.reject(
          this.managerError("Token Keycloak sem identificador de usuario")
        );
      }

      storageX.whereKeyIs(STORAGE_KEY_CD_USUARIO).set(username);
      const user = await this.userService.me();
      if (!user) {
        return Promise.reject(
          this.managerError("Nao foi possivel obter o usuario pelo Keycloak")
        );
      }

      this.persistUserMetadata(user);
      storageX.whereKeyIs(STORAGE_KEY_CURRENT_USER).set(user);
      this.currentUserSubject.next(user);
      return user;
    } catch (error) {
      return Promise.reject(this.managerError(error));
    }
  }

  public initListenerToken(): void {
    interval(this.intervalTimeInMilliseconds).subscribe(
      (x) => {
        console.log("--> Refresh de token iniciada " + new Date());
        //this.gerarCode();
      },
      (error) => {
        throw new Error("Erro ao executar o listener do token");
      }
    );
  }

  /**
   * Encerra a sessão local e solicita logout no Keycloak (fim de sessão SSO).
   * A limpeza do storage ocorre antes do redirect do Keycloak para não depender
   * de código executado após o await (a página pode ser descarregada).
   * Não persistir o token WSO2 após removeAll: isso mantinha o gateway válido e,
   * com SSO ativo, o utilizador voltava a aparecer autenticado após o retorno.
   */
  async logout(): Promise<void> {
    storageX.removeAll();
    this.currentUserSubject.next(null as any);
    try {
      await this.keycloakAuthService.logout();
    } catch (error) {
      console.error("Falha no logout Keycloak", error);
    }
  }

  private persistUserMetadata(user: any): void {
    storageX
      .whereKeyIs(STORAGE_KEY_ID_MANAGER)
      .set(user.gestorId != null ? user.gestorId : "");
    storageX
      .whereKeyIs(STORAGE_KEY_ID_SUPERVISOR)
      .set(user.supervisorId != null ? user.supervisorId : "");
    storageX
      .whereKeyIs(STORAGE_KEY_UUID_MANAGER)
      .set(user.gestorUuid != null ? user.gestorUuid : "");
    storageX
      .whereKeyIs(STORAGE_KEY_UUID_SUPERVISOR)
      .set(user.supervisorUuid != null ? user.supervisorUuid : "");
    this.syncComandosEfetivosFromUser(user);
  }

  /**
   * Atualiza `comandosEfetivos` no storage a partir do perfil ativo em `usuarios/me`.
   *
   * @param user payload de `usuarios/me`
   */
  private syncComandosEfetivosFromUser(user: any): void {
    const perfilId = storageX.whereKeyIs(STORAGE_KEY_ID_PERFIL).get();
    const perfis = Array.isArray(user?.perfis) ? user.perfis : [];
    if (!perfis.length) {
      return;
    }
    const match = perfilId != null
      ? perfis.find((item) => String(item.id) === String(perfilId))
      : (perfis.length === 1 ? perfis[0] : null);
    if (!match) {
      return;
    }
    const comandosEfetivos = Array.isArray(match.comandosEfetivos)
      ? match.comandosEfetivos
      : [];
    storageX.whereKeyIs(STORAGE_KEY_COMANDOS_EFETIVOS).set(comandosEfetivos);
    storageX.whereKeyIs(STORAGE_KEY_CURRENT_PERFIL).set(match);
  }

  private managerError(error: any): ErrorResponse {
    this.errorHandler.handleError(error);
    const data = error?.response?.data || null;
    const status = error?.response?.status || null;

    if (status === 401) {
      handleUnauthorizedRedirect();
    }

    if (data) {
      return data;
    }
    if (typeof error === "string") {
      return {
        success: false,
        status,
        message: error,
      };
    }
    if (error?.message) {
      return {
        success: false,
        status,
        message: error.message,
      };
    }
    return {
      success: false,
      status,
      message: "Ocorreu um erro na requisição com o servidor",
    };
  }
}
